import { useEffect, useMemo, useState } from "react";
import { listPublishedReleaseNotes, releaseErrorMessage, sortPublishedReleases } from "../components/settings/releaseNotesStore";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Card,
  Center,
  Container,
  Divider,
  Grid,
  Group,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconAdjustments,
  IconArrowLeft,
  IconArrowUpRight,
  IconBell,
  IconBug,
  IconCalendar,
  IconCheck,
  IconHistory,
  IconRocket,
  IconSearch,
  IconShieldCheck,
  IconSparkles,
} from "@tabler/icons-react";

/**
 * SMS Grup Portal - Sürüm Notları (kullanıcı görünümü).
 * Yalnızca yönetim sayfasında yayınlanan sürümler görüntülenir.
 * Backend gelene kadar kayıtlar aynı tarayıcının localStorage alanında tutulur.
 */
const HOME_PATH = "/";

const TYPE_INFO = {
  NEW: { label: "Yeni özellik", color: "teal", Icon: IconSparkles },
  IMPROVEMENT: {
    label: "İyileştirme",
    color: "blue",
    Icon: IconAdjustments,
  },
  FIX: { label: "Hata düzeltmesi", color: "orange", Icon: IconBug },
  SECURITY: { label: "Güvenlik", color: "grape", Icon: IconShieldCheck },
};

const dateLabel = (value) =>
  new Intl.DateTimeFormat("tr-TR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(`${value}T12:00:00`));

function ChangeItem({ item }) {
  const info = TYPE_INFO[item.type] || TYPE_INFO.IMPROVEMENT;
  const ChangeIcon = info.Icon;

  return (
    <Group align="flex-start" gap="md" wrap="nowrap">
      <ThemeIcon size={38} radius="md" color={info.color} variant="light">
        <ChangeIcon size={20} stroke={1.8} />
      </ThemeIcon>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group gap={7} mb={5}>
          <Badge size="sm" variant="light" color={info.color}>
            {info.label}
          </Badge>
          <Badge size="sm" variant="outline" color="gray">
            {item.module}
          </Badge>
        </Group>
        <Text fw={650} size="sm">
          {item.title}
        </Text>
        <Text size="sm" c="dimmed" mt={4} lh={1.65}>
          {item.description}
        </Text>
      </Box>
    </Group>
  );
}

function ReleaseCard({ release, isLatest, visibleItems }) {
  return (
    <Paper
      withBorder
      radius="lg"
      p={{ base: "lg", sm: "xl" }}
      style={{ borderColor: "var(--mantine-color-default-border)" }}
    >
      <Group justify="space-between" align="flex-start" mb="md">
        <Box style={{ flex: 1 }}>
          <Group gap="xs" mb="xs">
            <Badge color={isLatest ? "indigo" : "gray"} variant="light" radius="sm">
              v{release.version}
            </Badge>
            {isLatest && (
              <Badge color="teal" variant="light" radius="sm">
                En yeni sürüm
              </Badge>
            )}
          </Group>
          <Title order={3} size="h3" lh={1.3}>
            {release.title}
          </Title>
        </Box>
        <Group gap={5} c="dimmed" wrap="nowrap">
          <IconCalendar size={15} />
          <Text size="xs" style={{ whiteSpace: "nowrap" }}>
            {dateLabel(release.publishedAt)}
          </Text>
        </Group>
      </Group>

      <Text size="sm" c="dimmed" lh={1.7} mb="lg">
        {release.summary}
      </Text>
      <Divider mb="lg" />
      <Stack gap="xl">
        {visibleItems.map((item) => (
          <ChangeItem item={item} key={item.id} />
        ))}
      </Stack>
    </Paper>
  );
}

export default function ReleaseNotesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [module, setModule] = useState("ALL");
  const [type, setType] = useState("ALL");
  const [allReleases, setAllReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let active = true;
    listPublishedReleaseNotes()
      .then((entries) => { if (active) setAllReleases(sortPublishedReleases(entries)); })
      .catch((error) => { if (active) setLoadError(releaseErrorMessage(error)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const allItems = useMemo(
    () => allReleases.flatMap((release) => release.items),
    [allReleases]
  );
  const modules = useMemo(
    () => [...new Set(allItems.map((item) => item.module))].sort((a, b) => a.localeCompare(b, "tr")),
    [allItems]
  );

  const releases = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("tr-TR");
    return allReleases.map((release) => {
      const releaseMatches = [release.version, release.title, release.summary]
        .join(" ")
        .toLocaleLowerCase("tr-TR")
        .includes(query);
      const visibleItems = release.items.filter((item) => {
        const moduleMatches = module === "ALL" || item.module === module;
        const typeMatches = type === "ALL" || item.type === type;
        const queryMatches =
          !query ||
          releaseMatches ||
          [item.title, item.description, item.module, TYPE_INFO[item.type]?.label]
            .join(" ")
            .toLocaleLowerCase("tr-TR")
            .includes(query);
        return moduleMatches && typeMatches && queryMatches;
      });
      return { ...release, visibleItems };
    }).filter((release) => release.visibleItems.length > 0);
  }, [search, module, type, allReleases]);

  const latestRelease = allReleases[0] || null;
  const activeFilter = Boolean(search.trim() || module !== "ALL" || type !== "ALL");

  return (
    <Box bg="var(--mantine-color-body)" mih="100vh" py={{ base: "lg", md: 38 }}>
      <Container size="xl">
        <Group justify="space-between" align="center" mb="md" gap="sm">
          <Text size="sm" c="dimmed" fw={500}>
            SMS Grup Portal / Sürüm Notları
          </Text>
          <Button
            variant="light"
            color="indigo"
            radius="md"
            size="sm"
            leftSection={<IconArrowLeft size={17} />}
            onClick={() => navigate(HOME_PATH)}
            aria-label="Ana sayfaya dön"
          >
            Ana Sayfaya Dön
          </Button>
        </Group>
        {loadError && <Paper withBorder radius="md" p="md" mb="md"><Text c="red" size="sm">Sürüm notları yüklenemedi: {loadError}</Text></Paper>}
        {loading && <Text size="sm" c="dimmed" mb="md">Sürüm notları yükleniyor...</Text>}
        <Paper
          radius="xl"
          p={{ base: "xl", md: 38 }}
          mb="xl"
          style={{
            background:
              "linear-gradient(115deg, #19275c 0%, #314ba0 63%, #466ab8 100%)",
            color: "white",
            overflow: "hidden",
          }}
        >
          <Group justify="space-between" align="flex-start" gap="lg">
            <Box maw={710}>
              <Group gap="sm" mb="lg">
                <ThemeIcon size={44} radius="md" color="white" variant="white">
                  <IconRocket size={24} color="#314ba0" />
                </ThemeIcon>
                <Text fw={750} size="sm" style={{ letterSpacing: 1.1 }}>
                  SMS GRUP PORTAL
                </Text>
              </Group>
              <Title order={1} size="h1" style={{ color: "white" }}>
                Sürüm Notları
              </Title>
              <Text mt="sm" style={{ color: "rgba(255,255,255,.85)" }} lh={1.7}>
                Portal üzerindeki yenilikleri, iyileştirmeleri ve hata düzeltmelerini
                buradan takip edebilirsiniz.
              </Text>
              <Group mt="xl" gap="sm">
                {latestRelease ? (
                  <>
                    <Badge size="lg" variant="white" color="indigo" radius="sm">
                      Güncel sürüm: v{latestRelease.version}
                    </Badge>
                    <Badge
                      size="lg"
                      radius="sm"
                      variant="outline"
                      style={{ color: "white", borderColor: "rgba(255,255,255,.5)" }}
                    >
                      Son güncelleme: {dateLabel(latestRelease.publishedAt)}
                    </Badge>
                  </>
                ) : (
                  <Badge size="lg" variant="white" color="indigo" radius="sm">
                    Henüz yayınlanan sürüm yok
                  </Badge>
                )}
              </Group>
            </Box>
            <ThemeIcon
              size={70}
              radius="xl"
              color="white"
              variant="outline"
              style={{ borderColor: "rgba(255,255,255,.32)" }}
              visibleFrom="sm"
            >
              <IconHistory size={36} color="white" stroke={1.3} />
            </ThemeIcon>
          </Group>
        </Paper>

        <Paper withBorder radius="lg" p="lg" mb="xl">
          <Group justify="space-between" align="flex-end" mb="md">
            <Box>
              <Text fw={700}>Güncellemeleri keşfedin</Text>
              <Text size="xs" c="dimmed">
                Modüle, değişiklik türüne veya anahtar kelimeye göre filtreleyin.
              </Text>
            </Box>
            {activeFilter && (
              <Button
                variant="subtle"
                color="gray"
                size="xs"
                onClick={() => {
                  setSearch("");
                  setModule("ALL");
                  setType("ALL");
                }}
              >
                Filtreleri temizle
              </Button>
            )}
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            <TextInput
              aria-label="Sürüm notlarında ara"
              placeholder="Sürüm notlarında ara..."
              leftSection={<IconSearch size={17} />}
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
            />
            <Select
              aria-label="Modül filtresi"
              data={[
                { value: "ALL", label: "Tüm modüller" },
                ...modules.map((name) => ({ value: name, label: name })),
              ]}
              value={module}
              onChange={(value) => setModule(value || "ALL")}
              allowDeselect={false}
            />
            <Select
              aria-label="Değişiklik türü filtresi"
              data={[
                { value: "ALL", label: "Tüm değişiklikler" },
                ...Object.entries(TYPE_INFO).map(([value, info]) => ({
                  value,
                  label: info.label,
                })),
              ]}
              value={type}
              onChange={(value) => setType(value || "ALL")}
              allowDeselect={false}
            />
          </SimpleGrid>
        </Paper>

        <Grid gutter="xl" align="flex-start">
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Group justify="space-between" mb="md">
              <Title order={2} size="h3">
                Yayın geçmişi
              </Title>
              <Text size="sm" c="dimmed">
                {releases.length} sürüm listeleniyor
              </Text>
            </Group>
            {releases.length ? (
              <Stack gap="lg">
                {releases.map((release) => (
                  <ReleaseCard
                    key={release.id}
                    release={release}
                    visibleItems={release.visibleItems}
                    isLatest={release.id === latestRelease?.id}
                  />
                ))}
              </Stack>
            ) : (
              <Paper withBorder radius="lg" p="xl">
                <Center>
                  <Stack align="center" gap="xs" py="xl">
                    <ThemeIcon color="gray" variant="light" size="xl" radius="xl">
                      <IconSearch size={22} />
                    </ThemeIcon>
                    <Text fw={600}>{allReleases.length ? "Eşleşen sürüm notu bulunamadı" : "Henüz sürüm notu yayınlanmadı"}</Text>
                    <Text c="dimmed" size="sm" ta="center">
                      {allReleases.length ? "Başka bir arama veya filtre seçimi deneyebilirsiniz."
                        : "Yeni güncellemeler yayınlandığında burada listelenecektir."}
                    </Text>
                  </Stack>
                </Center>
              </Paper>
            )}
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Stack gap="md">
              <Card withBorder radius="lg" padding="lg">
                <Group gap="xs" mb="lg">
                  <ThemeIcon color="indigo" variant="light" radius="md">
                    <IconHistory size={19} />
                  </ThemeIcon>
                  <Text fw={700}>Sürümler</Text>
                </Group>
                <Stack gap="md">
                  {allReleases.map((release, index) => (
                    <Group key={release.id} wrap="nowrap" align="center" gap="sm">
                      <ThemeIcon
                        size={30}
                        radius="xl"
                        color={index === 0 ? "teal" : "gray"}
                        variant="light"
                      >
                        {index === 0 ? <IconCheck size={15} /> : <IconHistory size={15} />}
                      </ThemeIcon>
                      <Box style={{ flex: 1 }}>
                        <Text size="sm" fw={600}>
                          v{release.version}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {dateLabel(release.publishedAt)}
                        </Text>
                      </Box>
                      <Text size="xs" c="dimmed">
                        {release.items.length} kayıt
                      </Text>
                    </Group>
                  ))}
                  {!allReleases.length && <Text size="sm" c="dimmed">Henüz yayın yok.</Text>}
                </Stack>
              </Card>

              <Card withBorder radius="lg" padding="lg">
                <Group gap="xs" mb="sm">
                  <ThemeIcon color="blue" variant="light" radius="md">
                    <IconBell size={19} />
                  </ThemeIcon>
                  <Text fw={700}>Neler paylaşılıyor?</Text>
                </Group>
                <Stack gap="sm">
                  {Object.entries(TYPE_INFO).map(([key, info]) => {
                    const ItemIcon = info.Icon;
                    return (
                      <Group key={key} gap="xs">
                        <ItemIcon size={16} color={`var(--mantine-color-${info.color}-6)`} />
                        <Text size="sm" c="dimmed">
                          {info.label}
                        </Text>
                      </Group>
                    );
                  })}
                </Stack>
              </Card>

              <Paper bg="var(--mantine-color-indigo-light)" p="lg" radius="lg">
                <Group gap="sm" mb="xs">
                  <IconArrowUpRight size={18} color="var(--mantine-color-indigo-6)" />
                  <Text fw={700} size="sm" c="indigo">
                    Gelişim devam ediyor
                  </Text>
                </Group>
                <Text size="sm" c="dimmed" lh={1.6}>
                  Yeni özellikler ve iyileştirmeler yayınlandıkça bu alanda duyurulacak.
                </Text>
              </Paper>
            </Stack>
          </Grid.Col>
        </Grid>
        <Text c="dimmed" size="xs" ta="center" mt="xl">
          SMS Grup Portal · Sürüm Notları
        </Text>
      </Container>
    </Box>
  );
}
